import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  LoadingState,
  ErrorState,
  Modal,
  Input,
  Textarea,
  ConfirmDialog,
  IconButton,
  type ColumnDef,
} from '@/components/ui'
import {
  Tag, Plus, Pencil, Trash2, Copy, Layers,
  Users, Upload, Search, X, Zap, Sparkles, Loader2, Info, CheckCircle2, AlertCircle,
} from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import { formatCurrency, getErrorMessage } from '../shared.ts'

// ─── Types ───────────────────────────────────────────────────────────────────

interface PriceGroup {
  id: string
  name: string
  description: string | null
  codePattern: string | null
  aufschlag: string
  targetPriceCents: number | null
  isActive: boolean
  memberCount: number
  maxEkCents: number
  vkBerechnetCents: number | null
  berechnungCents: number | null
}

interface PriceGroupMember {
  id: string
  articleId: string
  itemCode: string
  itemName: string
  purchasePrice: number
  salesPrice: number
  addedManually: boolean
}

interface ArticleSearchResult {
  id: string
  itemCode: string
  itemName: string
}

interface AutoMatchResult {
  matched: ArticleSearchResult[]
  added: number
  skipped: number
}

interface PushResult {
  total: number
  successArticles: string[]
  failedArticles: Array<{ itemCode: string; error: string }>
  tenants: Array<{ tenantCode: string; success: number; failed: number }>
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fromCents(cents: number | null): string {
  if (cents === null) return ''
  return (cents / 100).toFixed(2)
}

function toCents(val: string): number | null {
  const n = parseFloat(val.replace(',', '.'))
  if (isNaN(n)) return null
  return Math.round(n * 100)
}

function formatAufschlag(aufschlag: string): string {
  const n = parseFloat(aufschlag)
  if (isNaN(n)) return '–'
  return `${(n * 100).toFixed(0)} %`
}

// ─── GroupForm ────────────────────────────────────────────────────────────────

interface GroupFormData {
  name: string
  description: string
  codePattern: string
  aufschlagPct: string
  targetPrice: string
}

function GroupFormModal({
  open,
  initial,
  onClose,
  onSave,
  saving,
}: {
  open: boolean
  initial?: Partial<GroupFormData>
  onClose: () => void
  onSave: (data: GroupFormData) => void
  saving: boolean
}) {
  const [form, setForm] = useState<GroupFormData>({
    name: initial?.name ?? '',
    description: initial?.description ?? '',
    codePattern: initial?.codePattern ?? '',
    aufschlagPct: initial?.aufschlagPct ?? '',
    targetPrice: initial?.targetPrice ?? '',
  })
  const [aiDescription, setAiDescription] = useState('')
  const [showAiInput, setShowAiInput] = useState(false)
  const [patternExplanation, setPatternExplanation] = useState<string | null>(null)
  const [explanationLoading, setExplanationLoading] = useState(false)

  const set = (k: keyof GroupFormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((f) => ({ ...f, [k]: e.target.value }))
    if (k === 'codePattern') setPatternExplanation(null)
  }

  const suggestMutation = useMutation({
    mutationFn: (desc: string) =>
      apiClient.post('/articles/price-groups/ai-suggest-pattern', { description: desc }).then((r) => r.data as { pattern: string; explanation: string }),
    onSuccess: (data) => {
      setForm((f) => ({ ...f, codePattern: data.pattern }))
      setPatternExplanation(data.explanation)
      setShowAiInput(false)
      setAiDescription('')
    },
  })

  async function explainPattern() {
    if (!form.codePattern.trim()) return
    setExplanationLoading(true)
    try {
      const res = await apiClient.post('/articles/price-groups/ai-explain-pattern', { pattern: form.codePattern })
      setPatternExplanation(res.data.explanation)
    } catch {
      setPatternExplanation(null)
    } finally {
      setExplanationLoading(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={initial?.name ? 'Preisgruppe bearbeiten' : 'Preisgruppe erstellen'} size="md">
      <div className="space-y-4 p-4">
        <Input label="Name *" value={form.name} onChange={set('name')} placeholder="z.B. Patchkabel 2m" />
        <Textarea label="Beschreibung" value={form.description} onChange={set('description')} rows={2} />

        {/* Code-Muster mit KI-Unterstuetzung */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">Code-Muster (Regex)</label>
            <button
              type="button"
              onClick={() => setShowAiInput((v) => !v)}
              className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 font-medium"
            >
              <Sparkles className="w-3.5 h-3.5" />
              KI-Vorschlag
            </button>
          </div>

          {/* KI-Eingabe */}
          {showAiInput && (
            <div className="flex gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
              <input
                className="flex-1 text-sm bg-transparent border-none outline-none placeholder-amber-400"
                placeholder='z.B. "alle Patchkabel 2m, Codes starten mit 76402"'
                value={aiDescription}
                onChange={(e) => setAiDescription(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && aiDescription.trim() && suggestMutation.mutate(aiDescription)}
                autoFocus
              />
              <button
                type="button"
                onClick={() => suggestMutation.mutate(aiDescription)}
                disabled={!aiDescription.trim() || suggestMutation.isPending}
                className="text-xs font-medium text-amber-700 hover:text-amber-800 disabled:opacity-50 flex items-center gap-1"
              >
                {suggestMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Generieren
              </button>
            </div>
          )}
          {suggestMutation.isError && (
            <p className="text-xs text-red-600">{getErrorMessage(suggestMutation.error as Error)}</p>
          )}

          {/* Pattern-Eingabe */}
          <div className="flex gap-2">
            <input
              className="flex-1 px-3 py-2 text-sm font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="z.B. ^76402"
              value={form.codePattern}
              onChange={set('codePattern')}
            />
            {form.codePattern && (
              <button
                type="button"
                onClick={explainPattern}
                disabled={explanationLoading}
                className="px-2 text-gray-400 hover:text-brand-600"
                title="Muster erklären"
              >
                {explanationLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Info className="w-4 h-4" />}
              </button>
            )}
          </div>
          <p className="text-xs text-gray-400">Prefix oder regulärer Ausdruck · KI-Vorschlag oben nutzen oder direkt eingeben</p>

          {/* KI-Erklaerung des aktuellen Musters */}
          {patternExplanation && (
            <div className="flex items-start gap-1.5 text-xs text-brand-700 bg-brand-50 rounded-lg px-2.5 py-1.5">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              {patternExplanation}
            </div>
          )}
        </div>

        <Input
          label="Aufschlag %"
          type="number"
          value={form.aufschlagPct}
          onChange={set('aufschlagPct')}
          placeholder="z.B. 650"
          hint="Prozent-Aufschlag auf den höchsten EK für VK_berechnet"
        />
        <Input
          label="Ziel-VK (€)"
          type="number"
          value={form.targetPrice}
          onChange={set('targetPrice')}
          placeholder="z.B. 7.86"
          hint="Einheitlicher Verkaufspreis für alle Artikel der Gruppe"
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
          <Button variant="primary" onClick={() => onSave(form)} disabled={saving || !form.name}>
            {saving ? 'Speichern…' : 'Speichern'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

// ─── GroupDetail ──────────────────────────────────────────────────────────────

function GroupDetail({ group, onRefresh }: { group: PriceGroup; onRefresh: () => void }) {
  const queryClient = useQueryClient()
  const [addSearch, setAddSearch] = useState('')
  const [patternPreview, setPatternPreview] = useState<AutoMatchResult | null>(null)
  const [pushResult, setPushResult] = useState<PushResult | null>(null)

  const { data: members = [], isLoading: membersLoading } = useQuery<PriceGroupMember[]>({
    queryKey: ['price-group-members', group.id],
    queryFn: () => apiClient.get(`/articles/price-groups/${group.id}/members`).then((r) => r.data),
  })

  const { data: searchResults = [], isFetching: searching } = useQuery<ArticleSearchResult[]>({
    queryKey: ['article-search-for-pg', addSearch],
    queryFn: () =>
      addSearch.length >= 2
        ? apiClient.get('/articles/price-groups/search-articles', { params: { q: addSearch } }).then((r) => r.data)
        : Promise.resolve([]),
    enabled: addSearch.length >= 2,
  })

  const removeMutation = useMutation({
    mutationFn: (articleId: string) =>
      apiClient.delete(`/articles/price-groups/${group.id}/members/${articleId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['price-group-members', group.id] }),
  })

  const addMutation = useMutation({
    mutationFn: (articleIds: string[]) =>
      apiClient.post(`/articles/price-groups/${group.id}/members`, { articleIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-group-members', group.id] })
      queryClient.invalidateQueries({ queryKey: ['price-groups'] })
      setAddSearch('')
      onRefresh()
    },
  })

  const previewMutation = useMutation({
    mutationFn: () =>
      apiClient
        .post(`/articles/price-groups/${group.id}/auto-match`, { pattern: group.codePattern, apply: false })
        .then((r) => r.data as AutoMatchResult),
    onSuccess: setPatternPreview,
  })

  const applyMatchMutation = useMutation({
    mutationFn: () =>
      apiClient
        .post(`/articles/price-groups/${group.id}/auto-match`, { pattern: group.codePattern, apply: true })
        .then((r) => r.data as AutoMatchResult),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-group-members', group.id] })
      queryClient.invalidateQueries({ queryKey: ['price-groups'] })
      setPatternPreview(null)
      onRefresh()
    },
  })

  const pushMutation = useMutation({
    mutationFn: () =>
      apiClient.post(`/articles/price-groups/${group.id}/push-to-sap`).then((r) => r.data as PushResult),
    onSuccess: (data) => {
      setPushResult(data)
      queryClient.invalidateQueries({ queryKey: ['articles'] })
    },
  })

  const memberColumns: ColumnDef<PriceGroupMember>[] = [
    { accessorKey: 'itemCode', header: 'Code', size: 120 },
    { accessorKey: 'itemName', header: 'Bezeichnung' },
    {
      accessorKey: 'purchasePrice',
      header: 'EK',
      size: 90,
      cell: ({ row }) => formatCurrency(row.original.purchasePrice / 100),
    },
    {
      accessorKey: 'salesPrice',
      header: 'VK aktuell',
      size: 100,
      cell: ({ row }) => formatCurrency(row.original.salesPrice / 100),
    },
    {
      id: 'source',
      header: 'Quelle',
      size: 90,
      cell: ({ row }) => (
        <span className={cn('text-xs px-1.5 py-0.5 rounded-full', row.original.addedManually ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-500')}>
          {row.original.addedManually ? 'manuell' : 'Pattern'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      size: 48,
      cell: ({ row }) => (
        <IconButton
          icon={<X className="w-4 h-4" />}
          aria-label="Entfernen"
          size="xs"
          onClick={() => removeMutation.mutate(row.original.articleId)}
          disabled={removeMutation.isPending}
        />
      ),
    },
  ]

  return (
    <div className="space-y-3 mt-3">
      {/* Aktionsleiste oben */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Manuell hinzufuegen */}
        <div className="relative flex-1 min-w-48">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="Artikel suchen und hinzufügen…"
              value={addSearch}
              onChange={(e) => setAddSearch(e.target.value)}
            />
          </div>
          {addSearch.length >= 2 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {searching && <div className="px-3 py-2 text-sm text-gray-500">Suche…</div>}
              {!searching && searchResults.length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-500">Keine Ergebnisse</div>
              )}
              {searchResults.map((a) => (
                <button
                  key={a.id}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between gap-2"
                  onClick={() => addMutation.mutate([a.id])}
                  disabled={addMutation.isPending}
                >
                  <span className="font-mono text-xs text-gray-500 shrink-0">{a.itemCode}</span>
                  <span className="truncate">{a.itemName}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Pattern-Match */}
        {group.codePattern && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => previewMutation.mutate()}
            disabled={previewMutation.isPending}
          >
            <Zap className="w-4 h-4" />
            Pattern vorschau
          </Button>
        )}

        {/* SAP Push */}
        <Button
          variant="primary"
          size="sm"
          onClick={() => { setPushResult(null); pushMutation.mutate() }}
          disabled={pushMutation.isPending || !group.targetPriceCents}
        >
          <Upload className="w-4 h-4" />
          {pushMutation.isPending ? 'Pushe…' : 'VK + Texte → SAP'}
        </Button>
      </div>

      {/* Mitglieder-Tabelle */}
      {membersLoading ? (
        <LoadingState label="Lade Mitglieder…" />
      ) : members.length === 0 ? (
        <EmptyState title="Keine Artikel" description="Nutze das Pattern oder füge Artikel manuell hinzu." />
      ) : (
        <DataTable data={members} columns={memberColumns} pagination={{ pageSize: 20 }} />
      )}

      {/* Pattern-Vorschau */}
      {patternPreview && (
        <Card>
          <Card.Body>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">
                Pattern-Vorschau: <span className="font-mono text-brand-600">{group.codePattern}</span>
                {' '}→ {patternPreview.matched.length} Artikel gefunden
              </span>
              <div className="flex gap-2">
                <Button variant="ghost" size="xs" onClick={() => setPatternPreview(null)}>Verwerfen</Button>
                <Button
                  variant="primary"
                  size="xs"
                  onClick={() => applyMatchMutation.mutate()}
                  disabled={applyMatchMutation.isPending || patternPreview.matched.length === 0}
                >
                  {applyMatchMutation.isPending ? 'Anwenden…' : 'Anwenden'}
                </Button>
              </div>
            </div>
            <div className="text-xs text-gray-500 max-h-32 overflow-y-auto space-y-0.5 font-mono">
              {patternPreview.matched.map((a) => (
                <div key={a.id}>{a.itemCode} – {a.itemName}</div>
              ))}
              {patternPreview.matched.length === 0 && <div className="text-amber-600">Kein Artikel passt zum Muster.</div>}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Push-Ergebnis */}
      {pushResult && (
        <Card>
          <Card.Body>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Push-Ergebnis</span>
              <IconButton icon={<X className="w-4 h-4" />} aria-label="Schließen" size="xs" onClick={() => setPushResult(null)} />
            </div>
            <div className="text-sm space-y-1">
              <div className="text-green-700">✓ {pushResult.successArticles.length} von {pushResult.total} Artikel erfolgreich</div>
              {pushResult.failedArticles.length > 0 && (
                <div className="text-red-600">✗ {pushResult.failedArticles.length} Fehler</div>
              )}
              <div className="text-xs text-gray-500 mt-1">
                {pushResult.tenants.map((t) => (
                  <span key={t.tenantCode} className="mr-3">{t.tenantCode}: {t.success} ok{t.failed > 0 && `, ${t.failed} Fehler`}</span>
                ))}
              </div>
              {pushResult.failedArticles.length > 0 && (
                <details className="mt-2">
                  <summary className="text-xs text-red-600 cursor-pointer">Fehlerdetails</summary>
                  <div className="mt-1 space-y-0.5 text-xs font-mono text-red-700">
                    {pushResult.failedArticles.map((f, i) => (
                      <div key={i}>{f.itemCode}: {f.error}</div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          </Card.Body>
        </Card>
      )}

      {addMutation.isError && (
        <div className="text-xs text-red-600">Fehler: {getErrorMessage(addMutation.error as Error)}</div>
      )}
      {pushMutation.isError && (
        <div className="text-xs text-red-600">Push-Fehler: {getErrorMessage(pushMutation.error as Error)}</div>
      )}
    </div>
  )
}

// ─── AiSuggestModal ───────────────────────────────────────────────────────────

interface AiSuggestion {
  prefix: string
  suggestedName: string
  suggestedPattern: string
  reason: string
  confidence: 'high' | 'medium' | 'low'
  articleCount: number
  sampleArticles: Array<{ itemCode: string; itemName: string; purchasePriceCents: number }>
  avgEkCents: number
  priceVariancePct: number
}

function AiSuggestModal({ open, onClose, onCreate }: {
  open: boolean
  onClose: () => void
  onCreate: (suggestion: AiSuggestion) => void
}) {
  const [loading, setLoading] = useState(false)
  const [suggestions, setSuggestions] = useState<AiSuggestion[]>([])
  const [error, setError] = useState<string | null>(null)
  const [created, setCreated] = useState<Set<string>>(new Set())

  async function load() {
    setLoading(true)
    setError(null)
    setSuggestions([])
    try {
      const res = await apiClient.post('/articles/price-groups/ai-suggest-groups')
      setSuggestions(res.data)
      if (res.data.length === 0) setError('Keine neuen Preisgruppen-Vorschläge gefunden – alle geeigneten Artikel sind bereits in Gruppen.')
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Unbekannter Fehler'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  function handleCreate(s: AiSuggestion) {
    onCreate(s)
    setCreated((c) => new Set([...c, s.prefix]))
  }

  const confidenceColor = { high: 'text-green-700 bg-green-50 border-green-200', medium: 'text-amber-700 bg-amber-50 border-amber-200', low: 'text-gray-600 bg-gray-50 border-gray-200' }
  const confidenceLabel = { high: 'Hoch', medium: 'Mittel', low: 'Niedrig' }

  return (
    <Modal open={open} onClose={onClose} title="KI-Vorschläge für Preisgruppen" size="xl">
      <div className="p-4 space-y-4">
        {!loading && suggestions.length === 0 && !error && (
          <div className="text-center py-6">
            <Sparkles className="w-10 h-10 text-amber-400 mx-auto mb-3" />
            <p className="text-sm text-gray-600 mb-4">
              Die KI analysiert alle Artikel nach Ähnlichkeiten im Code-Muster und Preisstruktur
              und schlägt geeignete Preisgruppen vor – z.B. Kabel mit Farbvarianten.
            </p>
            <Button variant="primary" onClick={load}>
              <Sparkles className="w-4 h-4" />
              Analyse starten
            </Button>
          </div>
        )}

        {loading && (
          <div className="text-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-brand-500 mx-auto mb-3" />
            <p className="text-sm text-gray-500">KI analysiert Artikelstamm…</p>
          </div>
        )}

        {error && (
          <>
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              {error}
            </div>
            <div className="flex justify-between items-center pt-2 border-t">
              <button
                type="button"
                onClick={load}
                disabled={loading}
                className="text-sm text-brand-600 hover:text-brand-700 font-medium disabled:opacity-50"
              >
                Neu analysieren
              </button>
              <Button variant="secondary" onClick={onClose}>Schließen</Button>
            </div>
          </>
        )}

        {suggestions.length > 0 && (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {suggestions.map((s) => (
              <div key={s.prefix} className="border border-gray-200 rounded-lg p-3 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900">{s.suggestedName}</span>
                      <span className={cn('text-xs px-1.5 py-0.5 rounded border font-medium', confidenceColor[s.confidence])}>
                        {confidenceLabel[s.confidence]}
                      </span>
                    </div>
                    <div className="text-xs font-mono text-gray-500 mt-0.5">{s.suggestedPattern}</div>
                    <div className="text-xs text-gray-500 mt-1">{s.reason}</div>
                  </div>
                  <div className="text-right shrink-0 text-xs text-gray-500">
                    <div>{s.articleCount} Artikel</div>
                    <div>⌀ EK {formatCurrency(s.avgEkCents / 100)}</div>
                    <div>Varianz {s.priceVariancePct}%</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {s.sampleArticles.slice(0, 4).map((a) => (
                    <span key={a.itemCode} className="text-xs font-mono bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                      {a.itemCode}
                    </span>
                  ))}
                  {s.articleCount > 4 && <span className="text-xs text-gray-400">+{s.articleCount - 4} weitere</span>}
                </div>
                <div className="flex justify-end">
                  {created.has(s.prefix) ? (
                    <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                      <CheckCircle2 className="w-3.5 h-3.5" />Angelegt
                    </span>
                  ) : (
                    <Button variant="primary" size="xs" onClick={() => handleCreate(s)}>
                      <Plus className="w-3 h-3" />
                      Preisgruppe anlegen
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {suggestions.length > 0 && (
          <div className="flex justify-between items-center pt-2 border-t">
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="text-sm text-brand-600 hover:text-brand-700 font-medium disabled:opacity-50"
            >
              Neu analysieren
            </button>
            <Button variant="secondary" onClick={onClose}>Schließen</Button>
          </div>
        )}
      </div>
    </Modal>
  )
}

// ─── VariantenModal ───────────────────────────────────────────────────────────

interface VariantRow {
  name: string
  codePattern: string
  aufschlagPct: string
  targetPrice: string
}

function VariantenModal({
  open,
  source,
  onClose,
  onDone,
}: {
  open: boolean
  source: PriceGroup
  onClose: () => void
  onDone: () => void
}) {
  const defaultAufschlag = String(Math.round(parseFloat(source.aufschlag) * 100))

  const [rows, setRows] = useState<VariantRow[]>(() => [
    { name: source.name, codePattern: source.codePattern ?? '', aufschlagPct: defaultAufschlag, targetPrice: fromCents(source.targetPriceCents) },
    { name: '', codePattern: '', aufschlagPct: defaultAufschlag, targetPrice: fromCents(source.targetPriceCents) },
  ])
  const [results, setResults] = useState<Array<'pending' | 'ok' | 'error'>>([])
  const [running, setRunning] = useState(false)
  const [done, setDone] = useState(false)

  function updateRow(i: number, k: keyof VariantRow, v: string) {
    setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, [k]: v } : r))
  }

  function addRow() {
    const last = rows[rows.length - 1]
    setRows((rs) => [...rs, { name: '', codePattern: last?.codePattern ?? '', aufschlagPct: last?.aufschlagPct ?? defaultAufschlag, targetPrice: last?.targetPrice ?? '' }])
  }

  function removeRow(i: number) {
    setRows((rs) => rs.filter((_, idx) => idx !== i))
  }

  async function createAll() {
    const validRows = rows.filter((r) => r.name.trim())
    if (validRows.length === 0) return
    setResults(validRows.map(() => 'pending'))
    setRunning(true)
    const newResults: Array<'ok' | 'error'> = []
    for (let i = 0; i < validRows.length; i++) {
      const r = validRows[i]
      const aufschlag = parseFloat(r.aufschlagPct) / 100
      try {
        await apiClient.post('/articles/price-groups', {
          name: r.name.trim(),
          description: source.description ?? null,
          codePattern: r.codePattern.trim() || null,
          aufschlag: isNaN(aufschlag) ? 0 : aufschlag,
          targetPriceCents: toCents(r.targetPrice),
        })
        newResults.push('ok')
      } catch {
        newResults.push('error')
      }
      setResults([...newResults, ...validRows.slice(newResults.length).map(() => 'pending' as const)])
    }
    setRunning(false)
    setDone(true)
    onDone()
  }

  const validRows = rows.filter((r) => r.name.trim())

  return (
    <Modal open={open} onClose={onClose} title={`Varianten anlegen – ${source.name}`} size="xl">
      <div className="space-y-4 p-4">
        <p className="text-sm text-gray-500">
          Erstelle mehrere Preisgruppen auf einmal – z.B. für verschiedene Kabellängen.
        </p>

        <div className="space-y-2">
          {/* Header */}
          <div className="grid grid-cols-[1fr_150px_80px_90px_32px] gap-2 px-1">
            <span className="text-xs font-medium text-gray-500">Name</span>
            <span className="text-xs font-medium text-gray-500">Code-Muster (Regex)</span>
            <span className="text-xs font-medium text-gray-500">Aufschlag %</span>
            <span className="text-xs font-medium text-gray-500">Ziel-VK (€)</span>
            <span />
          </div>

          {rows.map((row, i) => (
            <div key={i} className="grid grid-cols-[1fr_150px_80px_90px_32px] gap-2 items-center">
              <div className="flex items-center gap-1.5">
                {results[i] === 'ok' && <span className="text-green-600 text-sm shrink-0">✓</span>}
                {results[i] === 'error' && <span className="text-red-600 text-sm shrink-0">✗</span>}
                {results[i] === 'pending' && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400 shrink-0" />}
                <input
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50"
                  placeholder="z.B. Patchkabel 3m"
                  value={row.name}
                  onChange={(e) => updateRow(i, 'name', e.target.value)}
                  disabled={running || done}
                />
              </div>
              <input
                className="w-full px-2.5 py-1.5 text-sm font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50"
                placeholder="z.B. ^76403"
                value={row.codePattern}
                onChange={(e) => updateRow(i, 'codePattern', e.target.value)}
                disabled={running || done}
              />
              <input
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50"
                placeholder="z.B. 601"
                type="number"
                value={row.aufschlagPct}
                onChange={(e) => updateRow(i, 'aufschlagPct', e.target.value)}
                disabled={running || done}
              />
              <input
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50"
                placeholder="z.B. 9.81"
                type="number"
                value={row.targetPrice}
                onChange={(e) => updateRow(i, 'targetPrice', e.target.value)}
                disabled={running || done}
              />
              <button
                type="button"
                onClick={() => removeRow(i)}
                disabled={rows.length <= 1 || running || done}
                className="p-1 text-gray-400 hover:text-red-500 disabled:opacity-30"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        {!done && (
          <button
            type="button"
            onClick={addRow}
            disabled={running}
            className="flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700 font-medium disabled:opacity-50"
          >
            <Plus className="w-3.5 h-3.5" />
            Zeile hinzufügen
          </button>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="secondary" onClick={onClose} disabled={running}>
            {done ? 'Schließen' : 'Abbrechen'}
          </Button>
          {!done && (
            <Button variant="primary" onClick={createAll} disabled={running || validRows.length === 0}>
              {running ? (
                <><Loader2 className="w-4 h-4 animate-spin" />Erstelle…</>
              ) : (
                <><Layers className="w-4 h-4" />{validRows.length} Gruppe{validRows.length !== 1 ? 'n' : ''} anlegen</>
              )}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}

// ─── Main Section ─────────────────────────────────────────────────────────────

export function ArticlePriceGroupsSection() {
  const queryClient = useQueryClient()
  const [editGroup, setEditGroup] = useState<PriceGroup | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [createInitial, setCreateInitial] = useState<Partial<GroupFormData> | undefined>()
  const [deleteTarget, setDeleteTarget] = useState<PriceGroup | null>(null)
  const [variantSource, setVariantSource] = useState<PriceGroup | null>(null)
  const [showAiSuggest, setShowAiSuggest] = useState(false)
  const [bulkAction, setBulkAction] = useState<'pattern' | 'sap' | null>(null)
  const [bulkResults, setBulkResults] = useState<Array<{ name: string; status: 'pending' | 'ok' | 'error'; detail?: string }>>([])
  const [bulkRunning, setBulkRunning] = useState(false)
  const [bulkDone, setBulkDone] = useState(false)

  async function runBulkAction(action: 'pattern' | 'sap') {
    const targets = action === 'pattern'
      ? groups.filter((g) => g.codePattern)
      : groups
    if (targets.length === 0) return
    setBulkResults(targets.map((g) => ({ name: g.name, status: 'pending' })))
    setBulkAction(action)
    setBulkRunning(true)
    setBulkDone(false)
    const next: Array<{ name: string; status: 'pending' | 'ok' | 'error'; detail?: string }> = [...targets.map((g) => ({ name: g.name, status: 'pending' as const }))]
    for (let i = 0; i < targets.length; i++) {
      const g = targets[i]
      try {
        if (action === 'pattern') {
          await apiClient.post(`/articles/price-groups/${g.id}/auto-match`, { pattern: g.codePattern, apply: true })
        } else {
          await apiClient.post(`/articles/price-groups/${g.id}/push-to-sap`)
        }
        next[i] = { name: g.name, status: 'ok' }
      } catch (e: unknown) {
        const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Fehler'
        next[i] = { name: g.name, status: 'error', detail: msg }
      }
      setBulkResults([...next])
    }
    setBulkRunning(false)
    setBulkDone(true)
    queryClient.invalidateQueries({ queryKey: ['price-groups'] })
  }

  const { data: groups = [], isLoading, error } = useQuery<PriceGroup[]>({
    queryKey: ['price-groups'],
    queryFn: () => apiClient.get('/articles/price-groups').then((r) => r.data),
  })

  const createMutation = useMutation({
    mutationFn: (data: object) => apiClient.post('/articles/price-groups', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-groups'] })
      setShowCreate(false)
      setCreateInitial(undefined)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: object }) =>
      apiClient.patch(`/articles/price-groups/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-groups'] })
      setEditGroup(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/articles/price-groups/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-groups'] })
      setDeleteTarget(null)
    },
  })

  function handleSave(form: { name: string; description: string; codePattern: string; aufschlagPct: string; targetPrice: string }) {
    const aufschlag = parseFloat(form.aufschlagPct.replace(',', '.')) / 100
    const targetPriceCents = toCents(form.targetPrice)
    const payload = {
      name: form.name,
      description: form.description || null,
      codePattern: form.codePattern || null,
      aufschlag: isNaN(aufschlag) ? 0 : aufschlag,
      targetPriceCents,
    }
    if (editGroup) {
      updateMutation.mutate({ id: editGroup.id, data: payload })
    } else {
      createMutation.mutate(payload)
    }
  }

  const columns: ColumnDef<PriceGroup>[] = [
    {
      accessorKey: 'name',
      header: 'Preisgruppe',
      cell: ({ row }) => (
        <div>
          <div className="font-medium text-gray-900">{row.original.name}</div>
          {row.original.codePattern && (
            <div className="text-xs font-mono text-gray-400 mt-0.5">{row.original.codePattern}</div>
          )}
        </div>
      ),
    },
    {
      id: 'memberCount',
      header: 'Artikel',
      size: 80,
      cell: ({ row }) => (
        <span className="flex items-center gap-1 text-sm">
          <Users className="w-3.5 h-3.5 text-gray-400" />
          {row.original.memberCount}
        </span>
      ),
    },
    {
      id: 'maxEk',
      header: 'Höchster EK',
      size: 110,
      cell: ({ row }) => row.original.maxEkCents > 0 ? formatCurrency(row.original.maxEkCents / 100) : '–',
    },
    {
      id: 'aufschlag',
      header: 'Aufschlag',
      size: 90,
      cell: ({ row }) => formatAufschlag(row.original.aufschlag),
    },
    {
      id: 'vkBerechnet',
      header: 'VK_berechnet',
      size: 115,
      cell: ({ row }) =>
        row.original.vkBerechnetCents !== null ? formatCurrency(row.original.vkBerechnetCents / 100) : '–',
    },
    {
      id: 'targetPrice',
      header: 'Ziel-VK',
      size: 100,
      cell: ({ row }) =>
        row.original.targetPriceCents !== null ? (
          <span className="font-semibold text-brand-700">{formatCurrency(row.original.targetPriceCents / 100)}</span>
        ) : (
          <span className="text-gray-400">–</span>
        ),
    },
    {
      id: 'berechnung',
      header: 'Diff.',
      size: 90,
      cell: ({ row }) => {
        const b = row.original.berechnungCents
        if (b === null) return '–'
        const euros = b / 100
        return (
          <span className={cn('text-sm', euros > 0 ? 'text-amber-600' : euros < 0 ? 'text-green-600' : 'text-gray-500')}>
            {euros > 0 ? '+' : ''}{formatCurrency(euros)}
          </span>
        )
      },
    },
    {
      id: 'actions',
      header: '',
      size: 120,
      cell: ({ row }) => (
        <div className="flex gap-1">
          <IconButton
            icon={<Pencil className="w-4 h-4" />}
            aria-label="Bearbeiten"
            size="xs"
            onClick={() => setEditGroup(row.original)}
          />
          <IconButton
            icon={<Copy className="w-4 h-4" />}
            aria-label="Duplizieren"
            size="xs"
            onClick={() => {
              const g = row.original
              setCreateInitial({
                name: `${g.name} (Kopie)`,
                description: g.description ?? '',
                codePattern: g.codePattern ?? '',
                aufschlagPct: String(Math.round(parseFloat(g.aufschlag) * 100)),
                targetPrice: fromCents(g.targetPriceCents),
              })
              setShowCreate(true)
            }}
          />
          <IconButton
            icon={<Layers className="w-4 h-4" />}
            aria-label="Varianten anlegen"
            size="xs"
            onClick={() => setVariantSource(row.original)}
          />
          <IconButton
            icon={<Trash2 className="w-4 h-4" />}
            aria-label="Löschen"
            size="xs"
            onClick={() => setDeleteTarget(row.original)}
          />
        </div>
      ),
    },
  ]

  if (isLoading) return <LoadingState label="Lade Preisgruppen…" />
  if (error) return <ErrorState title="Fehler beim Laden" description={getErrorMessage(error as Error)} />

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Tag className="w-5 h-5 text-brand-500" />
            Preisgruppen
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Artikel bündeln und einheitlichen VK setzen. Höchster EK der Gruppe bestimmt VK_berechnet.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => setShowAiSuggest(true)}>
            <Sparkles className="w-4 h-4" />
            KI-Vorschläge
          </Button>
          {groups.length > 0 && (
            <>
              <Button variant="secondary" size="sm" onClick={() => runBulkAction('pattern')} disabled={bulkRunning}>
                <Zap className="w-4 h-4" />
                Pattern → alle
              </Button>
              <Button variant="secondary" size="sm" onClick={() => runBulkAction('sap')} disabled={bulkRunning}>
                <Upload className="w-4 h-4" />
                Alle → SAP
              </Button>
            </>
          )}
          <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="w-4 h-4" />
            Preisgruppe anlegen
          </Button>
        </div>
      </div>

      {groups.length === 0 ? (
        <EmptyState
          title="Keine Preisgruppen"
          description="Erstelle eine Preisgruppe, um Artikel mit einheitlichem Preis zu bündeln."
          action={<Button variant="primary" size="sm" onClick={() => setShowCreate(true)}><Plus className="w-4 h-4" />Erste Preisgruppe anlegen</Button>}
        />
      ) : (
        <div className="space-y-0">
          <DataTable
            data={groups}
            columns={columns}
            pagination={{ pageSize: 50 }}
            getRowId={(row) => row.id}
            getRowCanExpand={() => true}
            renderExpandedRow={(row) => (
              <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50">
                <GroupDetail
                  group={row.original}
                  onRefresh={() => queryClient.invalidateQueries({ queryKey: ['price-groups'] })}
                />
              </div>
            )}
          />
        </div>
      )}

      {/* Create Modal */}
      <GroupFormModal
        key={createInitial ? JSON.stringify(createInitial) : 'create-empty'}
        open={showCreate}
        initial={createInitial}
        onClose={() => { setShowCreate(false); setCreateInitial(undefined) }}
        onSave={handleSave}
        saving={createMutation.isPending}
      />

      {/* Edit Modal */}
      {editGroup && (
        <GroupFormModal
          open={!!editGroup}
          initial={{
            name: editGroup.name,
            description: editGroup.description ?? '',
            codePattern: editGroup.codePattern ?? '',
            aufschlagPct: String(Math.round(parseFloat(editGroup.aufschlag) * 100)),
            targetPrice: fromCents(editGroup.targetPriceCents),
          }}
          onClose={() => setEditGroup(null)}
          onSave={handleSave}
          saving={updateMutation.isPending}
        />
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <ConfirmDialog
          open={!!deleteTarget}
          title="Preisgruppe löschen"
          message={`Soll die Preisgruppe „${deleteTarget.name}" wirklich gelöscht werden? Alle ${deleteTarget.memberCount} Zuordnungen werden entfernt.`}
          confirmLabel="Löschen"
          variant="danger"
          onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
          onCancel={() => setDeleteTarget(null)}
          loading={deleteMutation.isPending}
        />
      )}

      {/* KI-Vorschlaege Modal */}
      <AiSuggestModal
        open={showAiSuggest}
        onClose={() => setShowAiSuggest(false)}
        onCreate={(s) => {
          // Oeffne Create-Formular mit vorausgefuellten Werten aus dem Vorschlag
          setCreateInitial({
            name: s.suggestedName,
            description: s.reason,
            codePattern: s.suggestedPattern,
            aufschlagPct: '',
            targetPrice: '',
          })
          setShowAiSuggest(false)
          setShowCreate(true)
        }}
      />

      {/* Varianten Modal */}
      {variantSource && (
        <VariantenModal
          open={!!variantSource}
          source={variantSource}
          onClose={() => setVariantSource(null)}
          onDone={() => {
            queryClient.invalidateQueries({ queryKey: ['price-groups'] })
            setVariantSource(null)
          }}
        />
      )}

      {/* Bulk-Aktion Fortschritt */}
      {bulkAction && (
        <Modal
          open={!!bulkAction}
          onClose={() => { if (!bulkRunning) { setBulkAction(null); setBulkDone(false) } }}
          title={bulkAction === 'pattern' ? 'Pattern auf alle Gruppen anwenden' : 'Alle Preisgruppen → SAP'}
          size="md"
        >
          <div className="p-4 space-y-3">
            <div className="max-h-72 overflow-y-auto space-y-1">
              {bulkResults.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  {r.status === 'ok' && <span className="text-green-600 shrink-0">✓</span>}
                  {r.status === 'error' && <span className="text-red-600 shrink-0">✗</span>}
                  {r.status === 'pending' && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400 shrink-0" />}
                  <span className={r.status === 'error' ? 'text-red-700' : 'text-gray-700'}>{r.name}</span>
                  {r.detail && <span className="text-xs text-red-500 truncate">{r.detail}</span>}
                </div>
              ))}
            </div>
            {bulkDone && (
              <div className="text-sm text-gray-500 pt-1 border-t">
                {bulkResults.filter((r) => r.status === 'ok').length} erfolgreich,{' '}
                {bulkResults.filter((r) => r.status === 'error').length} Fehler
              </div>
            )}
            <div className="flex justify-end pt-1">
              <Button variant="secondary" onClick={() => { setBulkAction(null); setBulkDone(false) }} disabled={bulkRunning}>
                {bulkDone ? 'Schließen' : 'Abbrechen'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
